/**
 * Local-only favorites store. Pin up to 10 nav items at the top of the
 * sidebar. Persisted in localStorage so it survives reloads; mirrored
 * across tabs via the `storage` event.
 *
 * Backend-backed sync (matching office's `/user-prefs/favorites`) is a
 * follow-up — the data shape stays compatible so swapping later is a
 * single network call away.
 */

import { useEffect, useState } from 'react';

const STORAGE_KEY = 'hash_favorites';
const MAX_PINS = 10;

export interface Favorite {
  /** Either `nav:/path` (nav item) or `action:name` (custom action). */
  action_key: string;
  position: number;
}

function read(): Favorite[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((x: any) => typeof x?.action_key === 'string')
      .map((x: any, i: number) => ({
        action_key: x.action_key as string,
        position: typeof x.position === 'number' ? x.position : i,
      }))
      .sort((a, b) => a.position - b.position)
      .slice(0, MAX_PINS);
  } catch {
    return [];
  }
}

function write(items: Favorite[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    // Same-tab consumers (we use a custom event because `storage` only
    // fires across tabs, not within the writer).
    window.dispatchEvent(new CustomEvent('hash:favorites-changed'));
  } catch { /* ignore */ }
}

export function listFavorites(): Favorite[] {
  return read();
}

export function isFavorite(actionKey: string): boolean {
  return read().some((f) => f.action_key === actionKey);
}

export function pinFavorite(actionKey: string): Favorite[] {
  const current = read();
  if (current.find((f) => f.action_key === actionKey)) return current;
  if (current.length >= MAX_PINS) return current;
  const next: Favorite[] = [
    ...current,
    { action_key: actionKey, position: current.length },
  ];
  write(next);
  return next;
}

export function unpinFavorite(actionKey: string): Favorite[] {
  const next = read()
    .filter((f) => f.action_key !== actionKey)
    .map((f, i) => ({ ...f, position: i }));
  write(next);
  return next;
}

export function moveFavorite(actionKey: string, direction: -1 | 1): Favorite[] {
  const current = read();
  const idx = current.findIndex((f) => f.action_key === actionKey);
  if (idx < 0) return current;
  const target = idx + direction;
  if (target < 0 || target >= current.length) return current;
  const next = [...current];
  [next[idx], next[target]] = [next[target], next[idx]];
  const renumbered = next.map((f, i) => ({ ...f, position: i }));
  write(renumbered);
  return renumbered;
}

/** Re-render hook that listens for storage + same-tab change events. */
export function useFavorites(): Favorite[] {
  const [favorites, setFavorites] = useState<Favorite[]>(read);
  useEffect(() => {
    const sync = () => setFavorites(read());
    window.addEventListener('storage', sync);
    window.addEventListener('hash:favorites-changed', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('hash:favorites-changed', sync);
    };
  }, []);
  return favorites;
}

export const FAVORITES_MAX = MAX_PINS;
